import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { 
  Home, 
  BookOpen, 
  UserCheck, 
  GraduationCap, 
  BarChart3, 
  MessageSquare, 
  HelpCircle, 
  User, 
  LogOut,
  Trophy,
  Users,
  Image,
  Target
} from 'lucide-react';

const NavigationCarousel = ({ menuItems, onItemClick, isCollapsed = true, onToggleSidebar }) => {
  const carouselRef = useRef(null);
  const trackRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const location = useLocation();
  const { isDarkMode } = useTheme();

  // Function to get appropriate icon for each menu item
  const getIconForItem = (label) => {
    switch (label.toUpperCase()) {
      case 'HOME':
        return Home;
      case 'STUDY':
        return BookOpen;
      case 'COURSES':
        return BookOpen;
      case 'CHALLENGES':
        return Trophy;
      case 'STUDENTS':
        return UserCheck;
      case 'COMMUNITY':
        return Users;
      case 'FACULTY':
        return GraduationCap;
      case 'PIN UP':
        return Image;
      case 'PROGRESS':
        return Target;
      case 'ANALYTICS':
        return BarChart3;
      case 'MESSAGES':
        return MessageSquare;
      case 'SUPPORT':
        return HelpCircle;
      case 'PROFILE':
        return User;
      case 'LOG OUT':
        return LogOut;
      default:
        return Home;
    }
  };

  // Function to check if a menu item is active
  const isActiveItem = (item) => {
    const currentPath = location.pathname;
    return currentPath === item.path;
  };


  // Auto-play functionality - scroll vertically
  useEffect(() => {
    let rafId, last = 0;
    
    const step = (t) => {
      if (!last) last = t;
      const dt = Math.min(32, t - last); // clamp
      last = t;
      
      if (trackRef.current && isPlaying) {
        trackRef.current.scrollTop += 0.05 * dt; // speed (px/ms) - slower for vertical
        
        // loop when reaching the end
        if (trackRef.current.scrollTop + trackRef.current.clientHeight >= trackRef.current.scrollHeight - 2) {
          trackRef.current.scrollTop = 0;
        }
      }
      
      rafId = requestAnimationFrame(step);
    };

    if (isPlaying) {
      rafId = requestAnimationFrame(step);
    }

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [isPlaying]);




  // Drag to scroll - vertical
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    let isDown = false;
    let startY = 0;
    let startTop = 0;

    const handlePointerDown = (e) => {
      // Don't start drag if clicking on a nav-card
      if (e.target.closest('.nav-card')) {
        return;
      }
      isDown = true;
      startY = e.clientY;
      startTop = track.scrollTop;
      track.setPointerCapture(e.pointerId);
      setIsPlaying(false);
    };

    const handlePointerMove = (e) => {
      if (!isDown) return;
      track.scrollTop = startTop - (e.clientY - startY);
    };

    const handlePointerUp = () => {
      isDown = false;
      setIsPlaying(true);
    };

    const handlePointerCancel = () => {
      isDown = false;
      setIsPlaying(true);
    };

    track.addEventListener('pointerdown', handlePointerDown);
    track.addEventListener('pointermove', handlePointerMove);
    track.addEventListener('pointerup', handlePointerUp);
    track.addEventListener('pointercancel', handlePointerCancel);

    return () => {
      track.removeEventListener('pointerdown', handlePointerDown);
      track.removeEventListener('pointermove', handlePointerMove);
      track.removeEventListener('pointerup', handlePointerUp);
      track.removeEventListener('pointercancel', handlePointerCancel);
    };
  }, []);

  return (
    <div 
      className={`fixed top-0 left-0 bg-[#AC5757] rounded-r-lg overflow-visible transition-all duration-300 ease-in-out h-screen z-50 ${
        isCollapsed ? 'w-20' : 'w-56'
      }`}
      style={{
        borderRadius: '0 18px 18px 0',
        boxShadow: 'none'
      }}
    >
      {/* Toggle Button - Fixed at top */}
      {onToggleSidebar && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-10">
          <button
            onClick={onToggleSidebar}
            className="bg-white/20 border-none text-white text-lg cursor-pointer p-2 rounded-lg transition-all duration-200 flex items-center justify-center w-10 h-10 hover:bg-white/30 shadow-lg"
          >
            {isCollapsed ? '▶' : '◀'}
          </button>
        </div>
      )}

      <div 
        ref={carouselRef}
        className="h-full"
      >
        <div 
          ref={trackRef} 
          className="flex flex-col gap-3 items-stretch overflow-y-auto overflow-x-hidden scroll-smooth py-5 px-2 h-full [&::-webkit-scrollbar]:hidden"
          style={{
            scrollSnapType: 'y mandatory',
            paddingTop: '10vh',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}
        >
          {menuItems.map((item, index) => {
            const IconComponent = getIconForItem(item.label);
            const isActive = isActiveItem(item);
            return (
              <div
                key={index}
                className={`nav-card flex-none h-16 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md flex items-center justify-center`}
                style={{
                  scrollSnapAlign: 'start'
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  console.log('Nav card clicked:', item);
                  onItemClick(item);
                }}
              >
                <div 
                  className={`h-full w-full rounded-xl px-3 py-0 flex items-center gap-3 transition-all duration-200 ${
                    isCollapsed ? 'justify-center' : 'justify-start'
                  } ${
                    isActive 
                      ? (isDarkMode ? 'text-white' : 'text-black') 
                      : 'bg-[#AC5757] text-white hover:bg-[#8A4A4A]'
                  }`}
                  style={{
                    backgroundColor: isActive ? (isDarkMode ? '#1f2937' : '#f3f4f6') : undefined
                  }}
                >
                  <IconComponent 
                    className={`w-8 h-8 flex-shrink-0 transition-all duration-300 hover:scale-110 ${
                      isActive ? (isDarkMode ? 'text-white' : 'text-black') : 'text-white'
                    }`}
                  />
                  <div 
                    className={`text-xs font-semibold text-left uppercase tracking-wide leading-tight whitespace-nowrap overflow-hidden text-ellipsis flex-1 transition-opacity duration-300 ${
                      isCollapsed ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100'
                    }`}
                    style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      letterSpacing: '0.04em',
                      lineHeight: '1.2'
                    }}
                  >
                    {item.label}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default NavigationCarousel;
